/**
 * Every element a web tour step can point at.
 *
 * The API's `Tours/tour-manifest.json` lists the same ids under `anchors.web`, and
 * `tourAnchors.manifest.test.ts` fails when an id here is missing there. A step whose anchor is not
 * in this list is dropped client-side (see `renderableSteps`), so a step authored for a control
 * this build does not have never points at nothing.
 *
 * Tag an element by spreading `tourAnchor(id)` onto it. In a list, tag the first row only: the
 * overlay spotlights the first visible match.
 */
export const TOUR_ANCHORS = {
  web: [
    // calls
    "calls.title",
    "calls.export",
    "calls.schedule",
    "calls.filters",
    "calls.list",
    "calls.row.open",
    "calls.row.dial",
    "calls.row.notes",
    "calls.row.complete",
    "calls.row.reschedule",
    "calls.row.open-player",
    // players
    "players.list",
    "players.teams",
    // On the page: the + button on the team chip row, which opens the Add team modal.
    "players.teams.add",
    // On the page: the Manage Teams button in the players toolbar.
    "players.teams.manage",
    // Inside the Manage teams slide panel. The panel stays mounted while closed, so the
    // element exists either way; closed it is aria-hidden and inert, which is what makes the
    // engine treat it as unreachable and fall back to a centred card.
    "players.teams.panel",
    // Inside the Manage teams slide panel: its close button, so a tour can hand the page back.
    "players.teams.panel.close",
    "players.search",
    "players.filter",
    // Inside the Filter Prospects modal: its Manage Tags button.
    "players.filter.manage-tags",
    // Inside the Filter Prospects modal: the scrolling body that holds every filter section —
    // Grad Year, Level, JC Year and the org's own tag categories — rather than any one of them,
    // since which sections render depends on the org's data and its tag definitions.
    "players.filter.options",
    // Inside the Filter Prospects modal: the Apply button in its footer, which commits the
    // selection and closes the modal.
    "players.filter.apply",
    // Inside the Filter Prospects modal: its close button, so Back can take the coach out of the
    // modal to the Filter step that opened it.
    "players.filter.close",
    // Inside the Customize Tags modal: the modal body, then the New Category control. The
    // control is the "+ Add New Category" button until it is pressed and the New Category form
    // that replaces it afterwards, so the anchor is on both branches and only one is ever live.
    "players.tags.customize",
    "players.tags.category",
    // Inside the Customize Tags modal: its close button, so a tour can hand the page back.
    "players.tags.close",
    "players.columns",
    // Inside the column settings popover: the tick list, and the first row's drag handle.
    "players.columns.visibility",
    "players.columns.reorder",
    "players.upload",
    "players.add",
    "players.row.open",
    "players.row.favorite",
    "players.row.note",
    "players.row.video",
    "players.row.tag",
    "players.row.select",
    // rosters
    // Roster boards list (/rosters): the "+ New roster" header button, and the whole list area
    // (both sections, or the empty-state block when there are no boards).
    "rosters.index.new",
    "rosters.index.list",
    "rosters.title",
    "rosters.tabs",
    // On the page: the Depth chart tab in the roster's section nav.
    "rosters.depth-chart",
    "rosters.limits",
    // On the page: the Scholarship & Roster Summary band above the board. It disappears when
    // the coach turns every summary card off, so a step on it is skipped rather than lost.
    "rosters.scholarship-total",
    "rosters.attach",
    "rosters.add",
    "rosters.export",
    "rosters.row.open",
    "rosters.row.status",
    "rosters.row.edit",
    "rosters.row.remove",
    "rosters.row.drag",
    // On the page: the Pitching staff and Lineups tabs in the roster's section nav.
    "rosters.pitching-tab",
    "rosters.lineups-tab",
    // Depth chart tab: the first draggable row's handle, the colour legend under the field, and
    // the Field/List layout toggle.
    "rosters.depth.drag",
    "rosters.depth.legend",
    "rosters.depth.layout",
    // Pitching staff tab: first handle, first role select, the pitch type totals strip.
    "rosters.pitching.drag",
    "rosters.pitching.role",
    "rosters.pitching.totals",
    // Lineups tab: each side's card, the first handle, the first bench toggle.
    "rosters.lineups.lhp",
    "rosters.lineups.rhp",
    "rosters.lineups.drag",
    "rosters.lineups.bench",
    // notes
    "notes.title",
    "notes.new",
    "notes.list",
    "notes.more",
    "notes.row.open",
    // duplicates
    "duplicates.title",
    "duplicates.tabs",
    "duplicates.list",
    "duplicates.bulk",
    "duplicates.row.select",
    "duplicates.row.dismiss",
    "duplicates.row.merge",
    // schedule
    "schedule.calendar",
    "schedule.new",
    "schedule.find-itinerary",
    "schedule.scope",
    "schedule.print",
    "schedule.updates",
    "schedule.row.open",
    "schedule.row.needs-setup",
    "schedule.row.unassigned",
    "schedule.catalog.list",
    "schedule.catalog.search",
    "schedule.catalog.row.add",
    "schedule.catalog.request",
    // video
    "video.grid",
    "video.search",
    "video.filters",
    "video.tags",
    "video.row.open",
    "video.row.clip",
    // roster-import
    "roster-import.type.options",
    "roster-import.type.juco",
    "roster-import.type.continue",
    "roster-import.mapping.columns",
    "roster-import.mapping.preview",
    "roster-import.mapping.reextract",
    "roster-import.team.search",
    "roster-import.team.follow",
    "roster-import.team.submit",
    "roster-import.review.names",
    "roster-import.review.juco",
    "roster-import.review.import",
    // build-itinerary
    "build-itinerary.event.source",
    "build-itinerary.event.name",
    "build-itinerary.match.summary",
    "build-itinerary.match.list",
    "build-itinerary.follow.list",
    "build-itinerary.follow.next",
    "build-itinerary.pitchers.list",
    "build-itinerary.pitchers.next",
    "build-itinerary.assign.auto",
    "build-itinerary.assign.pool",
    "build-itinerary.assign.finish",
  ],
} as const;

export type TourAnchorId = (typeof TOUR_ANCHORS.web)[number];

export const WEB_TOUR_ANCHORS: ReadonlySet<string> = new Set(TOUR_ANCHORS.web);

export const TOUR_ANCHOR_ATTRIBUTE = "data-tour-anchor";

/**
 * The only controls the tour may press on a coach's behalf: ones that open a popup, a panel or a
 * menu, or close one the tour itself opened.
 *
 * The anchor vocabulary also names destructive controls (`duplicates.row.dismiss`,
 * `rosters.row.remove`), and steps are authored in the API's manifest rather than here. Without an
 * allowlist, a manifest edit — a typo, or a well-meaning step written against the wrong anchor —
 * would be enough to make a browser press one of those for a coach who only came to read a tour.
 * Anything else falls back to "point at it and press Next", which loses nothing but a convenience.
 *
 * It lives here, beside the vocabulary it draws from, because three unrelated places ask it:
 * `useStepInteraction` (may this step press?), `TourOverlay` (is this step interactive at all?) and
 * the end-of-run tidy-up in `tourDom` (may the tour close this again?).
 */
export const AUTO_PRESS_ANCHORS: ReadonlySet<string> = new Set<TourAnchorId>([
  "players.columns",
  "players.filter",
  "players.teams.manage",
  "players.filter.manage-tags",
  "players.teams.panel.close",
  "players.tags.close",
  "players.filter.close",
]);

/**
 * Whether the tour may press this anchor for the coach.
 *
 * Asked by the overlay as well, which is the point: a step the allowlist refuses is not pressed,
 * so it must not be treated as interactive either. Without that, the shield comes off the page and
 * a destructive control sits spotlighted and live in a hole in the shade.
 */
export const canAutoPressAnchor = (anchorId: string): boolean => AUTO_PRESS_ANCHORS.has(anchorId);

/**
 * The close button of the popup each opener shows, for the popups whose opener says nothing about
 * being open (no `aria-expanded`), so pressing the opener again would not close them.
 *
 * The tour closes one of these for the coach when it moves on to a step that is not inside that
 * popup, and when the run ends. That is what lets the manifest go straight from the last step
 * inside the tag manager to "Choose your columns" without a "Close it when you are done" step
 * between them. Only a popup this run opened is closed: the opener's step has to be in the run's
 * record of presses. Both sides must also be in `AUTO_PRESS_ANCHORS`.
 */
export const POPUP_CLOSE_ANCHORS: ReadonlyMap<string, string> = new Map<TourAnchorId, TourAnchorId>([
  ["players.filter.manage-tags", "players.tags.close"],
  ["players.filter", "players.filter.close"],
  ["players.teams.manage", "players.teams.panel.close"],
]);

export const tourAnchor = (id: TourAnchorId): { "data-tour-anchor": TourAnchorId } => ({
  "data-tour-anchor": id,
});
